import type { Appointment, Type } from '@mend/types/appointment';
import type { User } from '@mend/types/user';
import type { Response } from '#types/common.ts';

import { api } from '#services/http-clients.ts';

type GroupedBy = 'hour' | 'day' | 'week' | 'month';
type BookingChannel = 'staff' | 'pss' | 'import' | null;

interface BaseAppointmentsParams {
  appointmentTypeType?: (Type | 'Imported')[];
  bookingChannel?: BookingChannel[];
  providerId?: number[];
  appointmentTypeId?: number[];
  addressId?: number[];
  /** Minimum start date in UTC with format `yyyy-MM-dd HH:mm:ss`. */
  minStartDate?: string;
  /** Maximum start date in UTC with format `yyyy-MM-dd HH:mm:ss`. */
  maxStartDate?: string;
  /** Minimum created date in UTC with format `yyyy-MM-dd HH:mm:ss`. */
  minCreated?: string;
  /** Maximum created date in UTC with format `yyyy-MM-dd HH:mm:ss`. */
  maxCreated?: string;
}

export interface GetAggregatedAppointmentDataParams extends BaseAppointmentsParams {
  timezone?: string;
}

export interface GetAppointmentSnapshotDataParams extends BaseAppointmentsParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  searchText?: string;
  appointmentVolumeEligibility?: boolean;
}

interface BaseFormsParams {
  providerId?: number[];
  assessmentTypeId?: number[];
  addressId?: number[];
  /** Minimum completed date in UTC with format `yyyy-MM-dd HH:mm:ss`. */
  minCompletedDate?: string;
  /** Maximum completed date in UTC with format `yyyy-MM-dd HH:mm:ss`. */
  maxCompletedDate?: string;
  /** Minimum created (assigned) date in UTC with format `yyyy-MM-dd HH:mm:ss`. */
  minCreated?: string;
  /** Maximum created (assigned) date in UTC with format `yyyy-MM-dd HH:mm:ss`. */
  maxCreated?: string;
  required?: 0 | 1;
  assessmentTypeRequired?: 1 | 0;
}

export interface GetAggregatedFormDataParams extends BaseFormsParams {
  timezone?: string;
}

export interface GetFormSnapshotDataParams extends BaseFormsParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  searchText?: string;
}

type TrendsData = {
  value: number | null;
  trend?: {
    rawValue: number;
    direction: 'up' | 'down' | 'neutral';
    comparisonPeriod: string;
    dataCompleteness: number;
  };
};

export type GetFormTrendsResponse = {
  groupedBy: GroupedBy;
  data: Array<{
    all: number;
    assigned: number;
    completed: number;
    canceled: number;
    period: string;
  }>;
};

export const getFormTrends = (
  params: GetAggregatedFormDataParams
): Promise<GetFormTrendsResponse> =>
  api.get<Response<GetFormTrendsResponse>>('/insights/form-trends', { params }).then((res) => {
    return res.data.payload;
  });

export type GetFormCompletionByTypeResponse = {
  groupedBy: GroupedBy;
  data: Array<{
    id: number;
    name: string;
    title: string;
    count: number;
    period: string;
  }>;
};
export const getFormCompletionByType = (
  params: GetAggregatedFormDataParams
): Promise<GetFormCompletionByTypeResponse> =>
  api
    .get<Response<GetFormCompletionByTypeResponse>>('/insights/form-completion-by-type', { params })
    .then((res) => {
      return res.data.payload;
    });

export type GetAppointmentRatesResponse = {
  groupedBy: GroupedBy;
  data: Array<{
    confirmationRate: number;
    noShowRate: number;
    cancellationRate: number;
    period: string;
  }>;
};
export const getAppointmentRates = (
  params: GetAggregatedAppointmentDataParams
): Promise<GetAppointmentRatesResponse> =>
  api
    .get<Response<GetAppointmentRatesResponse>>('/insights/appointment-rates', { params })
    .then((res) => {
      return res.data.payload;
    });

export type GetAppointmentsByTypeResponse = {
  groupedBy: GroupedBy;
  data: Array<{
    period: string;
    telehealthAppointments: number;
    officeVideoAppointments: number;
    phoneAppointments: number;
    inPersonAppointments: number;
    scheduledAppointments: number;
  }>;
};

export const getAppointmentsByType = (
  params: GetAggregatedAppointmentDataParams
): Promise<GetAppointmentsByTypeResponse> =>
  api
    .get<Response<GetAppointmentsByTypeResponse>>('/insights/appointments-by-type', { params })
    .then((res) => {
      return res.data.payload;
    });

export interface GetNoShowRateParams extends GetAggregatedAppointmentDataParams {
  minStartDate: string;
  maxStartDate: string;
}

export const getNoShowRate = (params: GetNoShowRateParams): Promise<TrendsData> =>
  api.get<Response<{ data: TrendsData }>>('/insights/no-show-rate', { params }).then((res) => {
    return res.data.payload.data;
  });

/**
 * TODO: Type is incomplete to follow the existing pattern of only using what
 * we need, keep updating as needed.
 */
export interface AppointmentSnapshot extends Pick<
  Appointment,
  'id' | 'startDate' | 'noShow' | 'created'
> {
  providerFirstName: User['firstName'];
  providerLastName: User['lastName'];
  patientFirstName: User['firstName'];
  patientLastName: User['lastName'];
  patientPreferredFirstName: User['preferredFirstName'];
  patientBirthDate: User['birthDate'];
  patientEmail: User['email'];
  patientPhone: User['mobile'];
  patientHasValidPhone: 0 | 1;
  patientChartId: User['chartId'];
  appointmentTypeType: Type;
  bookingChannel: NonNullable<BookingChannel>;
  /** Time between `startDate` and `created` in seconds. */
  leadTime: number;
  confirmedDate: Appointment['confirmed'];
}

type GetAppointmentSnapshotPayload = {
  data: AppointmentSnapshot[];
  page: number;
  totalItems: number;
};

export const getAppointmentSnapshotData = (
  params: GetAppointmentSnapshotDataParams
): Promise<GetAppointmentSnapshotPayload> =>
  api
    .get<Response<GetAppointmentSnapshotPayload>>('/insights/appointment-snapshot-data', { params })
    .then((res) => res.data.payload);

export interface GetTotalPatientsSeenParams extends GetAggregatedAppointmentDataParams {
  minStartDate: string;
  maxStartDate: string;
}

export const getTotalPatientsSeen = (params: GetTotalPatientsSeenParams): Promise<TrendsData> =>
  api
    .get<Response<{ data: TrendsData }>>('/insights/total-patients-seen', { params })
    .then((res) => {
      return res.data.payload.data;
    });

export interface GetFormCompletionTimeParams extends GetAggregatedFormDataParams {
  minCreated: string;
  maxCreated: string;
}

export const getFormCompletionTime = (params: GetFormCompletionTimeParams): Promise<TrendsData> =>
  api
    .get<Response<{ data: TrendsData }>>('/insights/form-completion-time', { params })
    .then((res) => {
      return res.data.payload.data;
    });

export type GetActiveProvidersResponse = {
  groupedBy: GroupedBy;
  data: Array<{
    activeProviders: number;
    period: string;
  }>;
};

export const getActiveProviders = (
  params: GetAggregatedAppointmentDataParams
): Promise<GetActiveProvidersResponse> =>
  api
    .get<Response<GetActiveProvidersResponse>>('/insights/active-providers', { params })
    .then((res) => {
      return res.data.payload;
    });

export type GetFormCompletionTimeTrendsResponse = {
  groupedBy: GroupedBy;
  data: Array<{
    completedAssessments: number;
    averageSecondsToComplete: number;
    medianSecondsToComplete: number;
    period: string;
  }>;
};

export const getFormCompletionTimeTrends = (
  params: GetAggregatedFormDataParams
): Promise<GetFormCompletionTimeTrendsResponse> =>
  api
    .get<Response<GetFormCompletionTimeTrendsResponse>>('/insights/form-completion-time-trend', {
      params,
    })
    .then((res) => {
      return res.data.payload;
    });

export type FormTypeBreakdownItem = {
  assessmentTypeId: number;
  assessmentTypeName: string;
  assessmentTypeRequired: 0 | 1;
  assigned: number;
  medianCompletionSeconds: number;
  completed: number;
  completionRate: number;
};

export const getFormTypeBreakdown = (
  params: GetAggregatedFormDataParams
): Promise<Array<FormTypeBreakdownItem>> =>
  api
    .get<
      Response<{
        data: Array<FormTypeBreakdownItem>;
      }>
    >('/insights/form-type-breakdown', { params })
    .then((res) => {
      return res.data.payload.data;
    });

export type FormStatusCountItem = {
  sort: number;
  status: string;
  count: number;
};

export type GetFormStatusCountsResponse = {
  data: FormStatusCountItem[];
};

export const getFormStatusCounts = (
  params: GetAggregatedFormDataParams
): Promise<GetFormStatusCountsResponse> =>
  api
    .get<Response<GetFormStatusCountsResponse>>('/insights/form-status-counts', { params })
    .then((res) => {
      return res.data.payload;
    });

export type FormSnapshot = {
  id: number;
  masterOrgId: number | null;
  masterOrgName: string | null;
  orgId: number;
  orgName: string;
  scoring: string | null;
  severity: number | null;
  reviewed: 0 | 1;
  reviewedDate: string | null;
  completed: 0 | 1;
  completedDate: string | null;
  canceled: 0 | 1;
  canceledDate: string | null;
  deleted: 0 | 1;
  created: string;
  updated: string | null;
  assessmentTypeId: number;
  assessmentTypeName: string;
  assessmentTypeTitle: string;
  providerId: number;
  providerFirstName: User['firstName'];
  providerLastName: User['lastName'];
  providerPreferredFirstName: User['preferredFirstName'];
  providerSpecialty: string | null;
  providerCredential: string | null;
  assigneeId: number;
  assigneeChartId: string | null;
  assigneeFirstName: User['firstName'];
  assigneeLastName: User['lastName'];
  assigneePreferredFirstName: User['preferredFirstName'];
  reviewerId: number | null;
  reviewerFirstName: User['firstName'];
  reviewerLastName: User['lastName'];
  reviewerPreferredFirstName: User['preferredFirstName'];
  assignmentSource: 'adhoc' | 'appointment';
  status: 'assigned' | 'opened' | 'started' | 'completed' | 'reviewed';
  subjectId: number;
  subjectChartId: string | null;
  subjectFirstName: User['firstName'];
  subjectLastName: User['lastName'];
  subjectPreferredFirstName: User['preferredFirstName'];
  addressId: number | null;
  addressType: string | null;
  addressLocationName: string | null;
  addressStreet: string | null;
  addressStreet2: string | null;
  addressCity: string | null;
  addressCounty: string | null;
  addressState: string | null;
  addressPostal: string | null;
  snapshotDate: string;
};

type GetFormSnapshotPayload = {
  data: FormSnapshot[];
  page: number;
  totalItems: number;
};

export const getFormSnapshotData = (
  params: GetFormSnapshotDataParams
): Promise<GetFormSnapshotPayload> =>
  api
    .get<Response<GetFormSnapshotPayload>>('/insights/form-snapshot-data', {
      params,
    })
    .then((res) => res.data.payload);

export interface GetFormsAssignedParams extends GetAggregatedFormDataParams {
  minCreated: string;
  maxCreated: string;
}

export const getFormsAssigned = (params: GetFormsAssignedParams): Promise<TrendsData> =>
  api
    .get<Response<{ data: TrendsData }>>('/insights/form-assignment-stats', { params })
    .then((res) => {
      return res.data.payload.data;
    });

export interface GetFormsCompletedParams extends GetAggregatedFormDataParams {
  minCompletedDate: string;
  maxCompletedDate: string;
}

export const getFormsCompleted = (params: GetFormsCompletedParams): Promise<TrendsData> =>
  api
    .get<Response<{ data: TrendsData }>>('/insights/form-completion-stats', { params })
    .then((res) => {
      return res.data.payload.data;
    });

export interface GetFormsCompletionRateParams extends GetAggregatedFormDataParams {
  minCreated: string;
  maxCreated: string;
}

export const getFormsCompletionRate = (params: GetFormsCompletionRateParams): Promise<TrendsData> =>
  api
    .get<Response<{ data: TrendsData }>>('/insights/form-completion-rate-stats', { params })
    .then((res) => {
      return res.data.payload.data;
    });

export interface GetTotalAppointmentsParams extends GetAggregatedAppointmentDataParams {
  minStartDate: string;
  maxStartDate: string;
}

export const getTotalAppointments = (params: GetTotalAppointmentsParams): Promise<TrendsData> =>
  api
    .get<Response<{ data: TrendsData }>>('/insights/scheduled-appointment-stats', { params })
    .then((res) => {
      return res.data.payload.data;
    });

export interface GetActiveProviderStatsParams extends GetAggregatedAppointmentDataParams {
  minStartDate: string;
  maxStartDate: string;
}

export const getActiveProviderStats = (params: GetActiveProviderStatsParams): Promise<TrendsData> =>
  api
    .get<Response<{ data: TrendsData }>>('/insights/active-provider-stats', { params })
    .then((res) => {
      return res.data.payload.data;
    });
